import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useOutletContext, Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ujmsWorkspace, ujmsIntakeConfig, ujmsCertifyDocket, ujmsUpdateTask, ujmsOpenDocument, ujmsCaseCharges,
  ujmsWorkproduct, ujmsAddWorkproduct, ujmsCasePretrial, ujmsCreateAssessment, ujmsAttachCondition,
  ujmsLogCompliance, ujmsTerminateCondition, ujmsConditionTypes, ujmsAiSummary, ujmsGenerateAiSummary,
  ujmsOpenFilingPdf, ujmsOpenOrderPdf, ujmsOpenOrderCertified,
  ujmsCaseCustody, ujmsBookPerson, ujmsReleasePerson, ujmsSearchCases, ujmsMergeCases,
  ujmsUnmergeCase, ujmsOverrideCustodyStatus, ujmsSetDocumentPublic, ujmsAddDocumentVersion, ujmsDocumentVersions,
  ujmsOpenDocumentVersion, ujmsCreateAttendanceLetter, ujmsAttendanceLetters, ujmsOpenAttendanceLetterPdf,
  ujmsOdysseyMeta,
} from "../../services/ujmsApi";
import { useAuth } from "../../context/AuthContext";
import { ChargeFormDialog, newChargeForm, amendChargeForm, AMENDABLE } from "../casetabs/ChargeFormDialog";
import { ChargeCorrectionRow } from "../casetabs/ChargeCorrectionRow";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { EvidenceTab } from "../casetabs/EvidenceTab";
import { DiscoveryTab } from "../casetabs/DiscoveryTab";
import { ProbationTab } from "../casetabs/ProbationTab";
import { FinanceTab } from "../casetabs/FinanceTab";
import { Panel, Badge, thCls, tdCls, fmtDT, fmtD, btnGhost, btnPrimary, btnDanger, inputCls, selectCls, Field, cls, can, isJudicial, EmptyState } from "../ui";
import { AddPartyDialog, ScheduleHearingDialog, AddFilingDialog, DocketEntryDialog, AssignDialog, StatusDialog, CorrectDocketDialog, DraftOrderDialog, BENCH_DOCKET_PRESETS } from "./CaseDialogs";
import { ManageHearingDialog, ConditionsTab } from "../ManageHearing";
import { CustodyBondTab } from "../CustodyBondTab";
import { DisposeDialog } from "./DisposeDialog";
import { JudicialFlowPanel } from "../JudicialFlow";
import { SentenceDialog, SealDialog, RedactDialog } from "./SentenceDialog";
import { DetailTab, EventsTab, ServiceTab, CausesTab, DispositionTab, NotesTab } from "../casetabs/OdysseyTabs";
import { SelectDocumentDialog, FindSessionsDialog, OdysseyRail } from "../OdysseyDialogs";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../../components/ui/dropdown-menu";
import { Gavel, FileText, Scale, ScrollText, ShieldAlert, Landmark, Sparkles, Loader2, Lock, Flag, Merge, BadgeCheck } from "lucide-react";

const CORE_TABS = ["Summary", "Flow", "Detail", "Parties", "Charges", "Events", "Service", "Hearings", "Conditions", "Notes"];
const MORE_TABS = ["Causes", "Disposition", "Docket", "Filings", "Motions", "Orders", "Documents", "Custody", "Tasks", "History"];

export default function CaseWorkspace() {
  const { id } = useParams();
  const { access } = useOutletContext();
  const [sp] = useSearchParams();
  const [ws, setWs] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState(sp.get("tab") || "Summary");
  const [meta, setMeta] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [showFilter, setShowFilter] = useState("ALL");
  const [hearingSort, setHearingSort] = useState("DATE");
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null);
  const [sealTarget, setSealTarget] = useState(null);
  const [redacting, setRedacting] = useState(null);
  const [docketAsc, setDocketAsc] = useState(false);
  const [correcting, setCorrecting] = useState(null);
  const [manageH, setManageH] = useState(null);
  const [flowOrderQuery, setFlowOrderQuery] = useState(null);
  const onFlowAction = (a) => {
    if (a.kind === "schedule") setDialog("hearing");
    else if (a.kind === "adjudicate_hearing") { const h = ws?.hearings?.find((x) => x.id === a.hearing_id); if (h) setManageH(h); else setTab("Hearings"); }
    else if (a.kind === "tab") setTab(a.tab);
    else if (a.kind === "sentence") setDialog("sentence");
    else if (a.kind === "dispose") setDialog("dispose");
    else if (a.kind === "order_form") setFlowOrderQuery(a.query);
    else if (a.kind === "refresh") load();
  };
  const [custody, setCustody] = useState(null);
  const [custodyDialog, setCustodyDialog] = useState(null);
  const [versionDoc, setVersionDoc] = useState(null);
  const { user } = useAuth();

  const load = useCallback(() => {
    ujmsWorkspace(id).then((r) => {
      setWs(r.data);
      try {
        const key = `ujms_recent_cases_${user?.id || "anon"}`;
        const cc = r.data.case;
        const list = JSON.parse(localStorage.getItem(key) || "[]").filter((x) => x.id !== cc.id);
        list.unshift({ id: cc.id, case_number: cc.case_number, caption: cc.caption, case_type: cc.case_type });
        localStorage.setItem(key, JSON.stringify(list.slice(0, 8)));
      } catch (e) { /* non-blocking */ }
    }).catch((e) => toast.error(e.response?.data?.detail || "Cannot open case"));
    ujmsCaseCustody(id).then((r) => setCustody(r.data)).catch(() => setCustody(null));
    ujmsOdysseyMeta(id).then((r) => setMeta(r.data)).catch(() => setMeta(null));
  }, [id, user?.id]);

  useEffect(() => { load(); ujmsIntakeConfig().then((r) => setCfg(r.data)).catch(() => {}); }, [load]);

  const issuedWarrants = useMemo(() => (ws?.warrants || []).filter((w) => w.status === "ISSUED"), [ws]);

  if (!ws) return <div className="text-sm text-slate-400 p-8">Loading case record…</div>;
  const c = ws.case;
  const docket = docketAsc ? [...ws.docket].reverse() : ws.docket;

  const certify = async (e) => {
    try { await ujmsCertifyDocket(id, e.id); toast.success(`Docket #${e.sequence_number} certified`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const completeTask = async (t) => {
    try { await ujmsUpdateTask(t.id, { status: "COMPLETED" }); toast.success("Task completed"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openTasks = ws.tasks.filter((t) => ["OPEN", "IN_PROGRESS"].includes(t.status));
  const nowIso = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  const filteredHearings = showFilter === "FUTURE"
    ? ws.hearings.filter((h) => (h.scheduled_start || "") >= nowIso)
    : ws.hearings;
  const visibleHearings = [...filteredHearings].sort(hearingSort === "TYPE"
    ? (a, b) => ((a.hearing_type_label || a.hearing_type || "").localeCompare(b.hearing_type_label || b.hearing_type || "")
        || (a.scheduled_start || "").localeCompare(b.scheduled_start || ""))
    : (a, b) => (a.scheduled_start || "").localeCompare(b.scheduled_start || ""));
  let railRecent = [];
  try { railRecent = JSON.parse(localStorage.getItem(`ujms_recent_cases_${user?.id || "anon"}`) || "[]"); } catch (e) { railRecent = []; }
  const moreTabs = [...MORE_TABS,
    ...(can(access, "evidence.view", "evidence.manage") ? ["Evidence"] : []),
    ...(can(access, "discovery.view") ? ["Discovery"] : []),
    ...(can(access, "pretrial.view") ? ["Pretrial"] : []),
    ...(can(access, "probation.view", "probation.manage") ? ["Probation"] : []),
    ...(can(access, "workproduct.manage") ? ["Work Product"] : []),
    ...(can(access, "finance.view", "finance.manage") ? ["Finance"] : [])];
  const coreTabs = CORE_TABS;

  return (
    <div className="flex gap-5 items-start" data-testid="case-workspace">
      <div className="flex-1 min-w-0 space-y-4">
      {/* Case header — Tyler Odyssey summary style */}
      {/* Classic Odyssey case banner */}
      <div className="ody2-banner flex items-center gap-3 px-3 py-1.5 rounded-t-sm" data-testid="case-banner">
        <button onClick={() => navigate("/ujms/cases")} className="text-white/80 hover:text-white text-[13px]" title="Back to case list" data-testid="banner-back">◄</button>
        <span className="text-white text-[16px] font-bold tracking-wide" style={{ textShadow: "0 1px 1px rgba(0,0,0,.35)" }}
          data-testid="banner-case-number">{c.case_number}</span>
        <div className="ml-auto flex items-center gap-1">
          <button className="ody2-btn-blue" onClick={() => setTab("Summary")} data-testid="banner-view">View</button>
          <button className="ody2-btn-blue" onClick={() => setTab("Documents")} data-testid="banner-scan">Scan</button>
          <button className="ody2-btn-blue" onClick={() => setTab("Filings")} data-testid="banner-import">Import</button>
          <button className="ody2-btn-blue" onClick={() => setDialog("selectDoc")} data-testid="banner-forms">Forms</button>
          {can(access, "case.modify") && <button className="ody2-btn-blue" onClick={() => setDialog("status")} data-testid="banner-status">Status</button>}
          {can(access, "order.sign", "charge.manage") && <button className="ody2-btn-blue" onClick={() => setDialog("ai")} data-testid="banner-bolt">Bolt Connect</button>}
          <span className="w-2" />
          <button className="ody2-btn-gray" disabled title="Changes save automatically" data-testid="banner-save">Save</button>
          <button className="ody2-btn-gray" onClick={() => navigate("/ujms/cases")} data-testid="banner-exit">Exit</button>
        </div>
      </div>
      <div className="bg-white border border-[#c8d4e0] rounded-sm shadow-sm px-5 py-4" data-testid="case-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-[300px]">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xl font-bold tracking-wide text-ujms" data-testid="case-header-number">{c.case_number}</span>
              {meta && <span className="text-[11px] text-slate-500 font-medium" data-testid="case-header-court-node">{meta.court_node}</span>}
              <Badge value={c.case_type} />
              {c.confidentiality_level !== "PUBLIC" && <Badge value={c.confidentiality_level} />}
              {custody && (custody.in_custody ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-red-700 text-white rounded-sm px-2 py-0.5" data-testid="case-custody-chip">
                  <ShieldAlert className="w-3 h-3" /> In Custody{custody.record?.facility ? ` — ${custody.record.facility}` : ""}
                </span>
              ) : custody.conditional ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-amber-600 text-white rounded-sm px-2 py-0.5" data-testid="case-custody-chip">
                  Conditional Release
                </span>
              ) : custody.last_release ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-emerald-700 text-white rounded-sm px-2 py-0.5" data-testid="case-custody-chip">
                  Released {fmtD(custody.last_release.released_at)}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-slate-200 text-slate-600 rounded-sm px-2 py-0.5" data-testid="case-custody-chip">
                  Not In Custody
                </span>
              ))}
            </div>
            {c.status === "MERGED" && (
              <div className="mt-2 flex items-center gap-2 bg-violet-700 text-white text-sm font-bold rounded-sm px-3 py-1.5" role="alert" data-testid="merged-banner">
                <Merge className="w-4 h-4" />
                THIS CASE WAS MERGED INTO{" "}
                <Link to={`/ujms/cases/${c.merged_into_case_id}`} className="underline" data-testid="merged-into-link">
                  {c.merged_into_case_number}
                </Link>
                <span className="font-normal text-white/80">— by {c.merged_by}, {fmtD(c.merged_at)}</span>
                {can(access, "docket.correct", "administration.manage") && (() => {
                  const hrs = (Date.now() - new Date(c.merged_at).getTime()) / 36e5;
                  return hrs <= 24 ? (
                    <button className="ml-auto bg-white text-violet-800 text-xs font-bold px-2.5 py-1 rounded-sm hover:bg-violet-100 transition-colors"
                      onClick={() => setDialog("unmerge")} data-testid="qa-undo-merge">
                      Undo Merge ({Math.max(1, Math.round(24 - hrs))}h left)
                    </button>
                  ) : (
                    <button className="ml-auto bg-violet-900/60 text-white/60 text-xs font-bold px-2.5 py-1 rounded-sm cursor-not-allowed" disabled
                      title="The 24-hour undo window has expired — this merge is permanent" data-testid="qa-undo-merge-expired">
                      Undo window expired
                    </button>
                  );
                })()}
              </div>
            )}
            {issuedWarrants.length > 0 && (
              <div className="mt-2 flex items-center gap-2 bg-red-600 text-white text-sm font-bold rounded-sm px-3 py-1.5 animate-pulse"
                role="alert" data-testid="active-warrant-banner">
                <ShieldAlert className="w-4 h-4" />
                ACTIVE WARRANT — {issuedWarrants.map((w) => `${w.warrant_type} (${w.person_name || "defendant"})`).join("; ")}
              </div>
            )}
            <h1 className="text-[17px] font-bold text-ujms-dark mt-1.5" data-testid="case-header-caption">{c.caption}</h1>
            <div className="text-xs text-slate-600 mt-0.5" data-testid="case-header-type-line">
              <span className="text-slate-400">Type</span> <b>{c.case_type_label || c.case_type}</b>
              {meta?.category && <><span className="text-slate-400 ml-3">Category</span> <b>{meta.category}</b></>}
            </div>
            {meta && meta.flags.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap mt-1.5" data-testid="case-flags-row">
                {meta.flags.map((f, i) => (
                  <span key={i} title={f.tooltip} data-testid={`case-flag-${i}`}
                    className="inline-flex items-center gap-1 text-[10px] font-bold text-white px-1.5 py-0.5 rounded-[2px] cursor-help"
                    style={{ background: f.color }}>
                    <Flag className="w-2.5 h-2.5" /> {f.label}
                  </span>
                ))}
              </div>
            )}
            {c.case_type === "CRIMINAL" && (
              <div className="text-xs text-slate-600 mt-0.5" data-testid="case-header-defendant">
                <span className="font-semibold">{c.primary_defendant || "—"}</span>
                {(() => { const dp = (ws.parties || []).find((p) => (p.relationship_type || "").toUpperCase() === "DEFENDANT"); return dp?.dob ? <span className="ml-2 text-slate-500">DOB {dp.dob}</span> : null; })()}
              </div>
            )}
          </div>
          <div className="text-sm min-w-[220px]" data-testid="case-header-status-block">
            {[["Status", <span className="font-bold text-[#8b1a1a]" data-testid="case-header-status" key="s">{(c.status || "").replace(/_/g, " ")}</span>],
              ["Case Type", c.case_type_label || c.case_type],
              ["Division", c.division_name],
              ["Judicial Officer", c.current_judge_name || "Unassigned"],
              ["Courtroom", c.current_courtroom_name || "—"],
              ["Next Court Date", c.next_hearing ? `${fmtDT(c.next_hearing.scheduled_start)} — ${c.next_hearing.hearing_type_label || c.next_hearing.hearing_type}` : "None set"],
              ["Filed", fmtD(c.filing_date)],
              ...(can(access, "finance.view", "finance.manage") ? [["Financial Balance",
                <button key="fb" className="tyler-link font-semibold" onClick={() => setTab("Finance")} data-testid="case-header-financial-balance">
                  {(meta?.financial_balance ?? 0).toFixed(2)}
                </button>]] : []),
              ...(c.disposition ? [["Disposition", `${c.disposition.label} (${c.disposition.method_label}, ${c.disposition.date})`]] : [])]
              .map(([k, v]) => (
                <div key={k} className="grid grid-cols-[130px_1fr] gap-2 py-0.5">
                  <span className="text-slate-500 text-right">{k}</span>
                  <span className="font-semibold text-slate-800" data-testid={k === "Judicial Officer" ? "case-header-judge" : k === "Next Court Date" ? "case-header-next-date" : k === "Disposition" ? "case-header-disposition" : undefined}>{v}</span>
                </div>
              ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#e3e9ef]" data-testid="case-quick-actions">
          {can(access, "filing.create") && <button className={btnGhost} onClick={() => setDialog("filing")} data-testid="qa-add-filing">Add Filing</button>}
          {can(access, "hearing.schedule") && <button className={btnGhost} onClick={() => setDialog("hearing")} data-testid="qa-schedule-hearing">Schedule Hearing</button>}
          {can(access, "case.modify") && <button className={btnGhost} onClick={() => setDialog("party")} data-testid="qa-add-party">Add Party</button>}
          {can(access, "docket.create") && <button className={btnGhost} onClick={() => setDialog("docket")} data-testid="qa-add-docket">Docket Entry</button>}
          {can(access, "case.assign") && <button className={btnGhost} onClick={() => setDialog("assign")} data-testid="qa-assign">Assign</button>}
          {can(access, "case.modify") && <button className={btnGhost} onClick={() => setDialog("status")} data-testid="qa-change-status">Change Status</button>}
          {can(access, "custody.manage", "order.sign") && custody && (custody.in_custody ? (
            <button className={btnDanger} onClick={() => setCustodyDialog("release")} data-testid="qa-release-custody">Release from Custody</button>
          ) : (
            <button className="inline-flex items-center gap-1.5 border border-red-300 bg-white text-red-700 text-sm font-medium px-3 py-1.5 rounded-sm hover:bg-red-50 transition-colors"
              onClick={() => setCustodyDialog("book")} data-testid="qa-book-custody">Book Into Custody</button>
          ))}
          {can(access, "order.sign") && custody && (custody.record || custody.conditional || custody.last_release) && (
            <button className="inline-flex items-center gap-1.5 border border-amber-400 bg-amber-50 text-amber-800 text-sm font-medium px-3 py-1.5 rounded-sm hover:bg-amber-100 transition-colors"
              onClick={() => setCustodyDialog("override")} data-testid="qa-override-custody">Override Custody Status</button>
          )}
          {can(access, "case.modify", "order.sign") && c.status !== "MERGED" && (
            <button className={btnGhost} onClick={() => setDialog("merge")} data-testid="qa-merge-case">
              <Merge className="w-3.5 h-3.5" /> Merge Case
            </button>
          )}
          {can(access, "hearing.manage", "order.sign") && (
            <button className={btnGhost} onClick={() => setDialog("attendance")} data-testid="qa-attendance-letter">
              <FileText className="w-3.5 h-3.5" /> Attendance Letter
            </button>
          )}
          {can(access, "order.sign", "case.modify") && (["DISPOSED", "CLOSED"].includes(c.status) ? (
            <button className="inline-flex items-center gap-1.5 border border-slate-300 bg-slate-100 text-slate-400 text-sm font-medium px-3 py-1.5 rounded-sm cursor-not-allowed"
              disabled title="This case has already been disposed. Use Change Status to reopen it if a disposition must be vacated."
              data-testid="qa-dispose-case-disabled">
              <Gavel className="w-3.5 h-3.5" /> Disposed
            </button>
          ) : (
            <button className={btnDanger} onClick={() => setDialog("dispose")} data-testid="qa-dispose-case">
              <Gavel className="w-3.5 h-3.5" /> Dispose Case
            </button>
          ))}
          {can(access, "order.sign") && c.case_type === "CRIMINAL" && (
            <button className="inline-flex items-center gap-1.5 border border-emerald-300 bg-emerald-50 text-emerald-700 text-sm font-medium px-3 py-1.5 rounded-sm hover:bg-emerald-100 transition-colors"
              onClick={() => setDialog("sentence")} data-testid="qa-enter-sentence">
              <Scale className="w-3.5 h-3.5" /> Enter Sentence
            </button>
          )}
          {can(access, "record.seal") && (
            <button className={btnGhost}
              onClick={() => setSealTarget({ kind: "case", id: c.id, label: c.case_number, sealed: (c.confidentiality_level || "PUBLIC") !== "PUBLIC" })}
              data-testid="qa-seal-case">
              <Lock className="w-3.5 h-3.5" /> {(c.confidentiality_level || "PUBLIC") !== "PUBLIC" ? "Unseal Case" : "Seal Case"}
            </button>
          )}
          {can(access, "order.sign", "charge.manage") && (
            <button className="inline-flex items-center gap-1.5 border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium px-3 py-1.5 rounded-sm hover:bg-amber-100 transition-colors"
              onClick={() => setDialog("ai")} data-testid="qa-ai-summary">
              <Sparkles className="w-3.5 h-3.5" /> AI Summary
            </button>
          )}
        </div>
      </div>

      {/* Tabs — classic Odyssey strip */}
      <div className="flex items-end gap-[3px] px-1 pt-1 border-b border-[#5b84b5] bg-transparent" data-testid="case-tabs">
        <span className="text-[#f28c28] text-[15px] font-black px-1 pb-1" aria-hidden="true">✱</span>
        {coreTabs.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`case-tab-${t.toLowerCase().replace(/ /g, "-")}`}
            className={cls("ody2-tab px-3.5 py-1.5 text-[12px] whitespace-nowrap -mb-px",
              tab === t && "ody2-tab-active")}>
            {t === "Finance" ? "Financial" : t === "Flow" ? "Judicial Flow" : t}
            {t === "Hearings" && <span className="ml-1 text-[10px] opacity-70">({ws.hearings.length})</span>}
            {t === "Tasks" && openTasks.length > 0 && <span className="ml-1 text-[10px] text-orange-700 font-bold">({openTasks.length})</span>}
          </button>
        ))}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="case-tab-more"
              className={cls("ody2-tab px-3 py-1.5 text-[12px] whitespace-nowrap flex items-center gap-1 -mb-px",
                moreTabs.includes(tab) && "ody2-tab-active")}>
              {moreTabs.includes(tab) ? tab : <span className="text-emerald-700 font-black">▶</span>}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[190px] rounded-sm">
            {moreTabs.map((t) => (
              <DropdownMenuItem key={t} className={cls("cursor-pointer text-[12px]", tab === t && "font-bold text-ujms")}
                onClick={() => setTab(t)} data-testid={`case-tab-${t.toLowerCase().replace(/ /g, "-")}`}>
                {t === "Finance" ? "Financial" : t === "Flow" ? "Judicial Flow" : t}
                {t === "Docket" && <span className="ml-auto text-[10px] text-slate-400">({ws.docket.length})</span>}
                {t === "Filings" && <span className="ml-auto text-[10px] text-slate-400">({ws.filings.length})</span>}
                {t === "Tasks" && openTasks.length > 0 && <span className="ml-auto text-[10px] text-orange-600 font-bold">({openTasks.length})</span>}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {tab === "Flow" && (
        <Panel title="Judicial Flow — stage of the case and the Court's next actions" testId="judicial-flow-tab">
          <div className="p-3"><JudicialFlowPanel caseId={id} access={access} onAction={onFlowAction} refreshKey={ws?.case?.updated_at} /></div>
        </Panel>
      )}
      {tab === "Summary" && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4" data-testid="ov-summary">
          <div className="xl:col-span-2 space-y-4">
            {(ws.charges || []).length > 0 && (
              <Panel title="charges" testId="ov-charges">
                <table className="w-full border-collapse">
                  <thead><tr><th className={thCls}>Description</th><th className={thCls}>Status</th><th className={thCls}>Class</th><th className={thCls}>Disposition</th></tr></thead>
                  <tbody>
                    {ws.charges.slice(0, 6).map((ch, i) => (
                      <tr key={ch.id || i} className="hover:bg-slate-50">
                        <td className={tdCls}>
                          <button className="tyler-charge-link text-left hover:opacity-80" onClick={() => setTab("Charges")} data-testid={`ov-charge-link-${i}`}>
                            {i + 1}. {(ch.description || "").toUpperCase()}
                          </button>
                          <div className="text-xs text-slate-500 font-mono">{ch.statute_code}</div>
                        </td>
                        <td className={tdCls}><Badge value={ch.status} /></td>
                        <td className={tdCls}>{ch.classification || "—"}</td>
                        <td className={tdCls}>{ch.disposition || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            )}
            <Panel title="most recent events & hearings" testId="ov-docket">
              <table className="w-full border-collapse"><tbody>
                {ws.docket.slice(0, 8).map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50"><td className={`${tdCls} font-mono text-xs whitespace-nowrap w-28`}>{e.date}</td>
                    <td className={tdCls}><span className="font-semibold text-slate-800">{e.event_code.replace(/_/g, " ")}</span><div className="text-xs text-slate-500 line-clamp-1">{e.description}</div></td></tr>
                ))}
              </tbody></table>
            </Panel>
            <Panel title="recent filings" testId="ov-filings">
              <table className="w-full border-collapse"><tbody>
                {ws.filings.slice(0, 5).map((f) => (
                  <tr key={f.id} className="hover:bg-slate-50"><td className={tdCls}>{f.filing_title || f.title}<div className="text-xs text-slate-400">{f.filing_type} · {f.filed_by}</div></td>
                    <td className={tdCls}><Badge value={f.status} /></td></tr>
                ))}
                {!ws.filings.length && <tr><td className={tdCls}>No filings.</td></tr>}
              </tbody></table>
            </Panel>
          </div>
          <div className="space-y-4">
            {c.case_type === "CRIMINAL" && (
              <Panel title="defendant demographics" testId="ov-demographics">
                <div className="p-3 text-sm space-y-1">
                  {(() => {
                    const dp = (ws.parties || []).find((p) => (p.relationship_type || "").toUpperCase() === "DEFENDANT");
                    return (
                      <>
                        <div className="font-bold text-slate-900">{dp?.display_name || c.primary_defendant || "—"}</div>
                        <div className="text-xs text-slate-600">DOB {dp?.dob || "—"}</div>
                        {custody?.in_custody && <div className="text-xs font-bold text-red-700">IN CUSTODY — {custody.record?.facility} (booking {custody.record?.booking_number})</div>}
                        {custody && !custody.in_custody && custody.last_release && <div className="text-xs text-emerald-700">Released {fmtDT(custody.last_release.released_at)} by {custody.last_release.released_by}</div>}
                      </>
                    );
                  })()}
                </div>
              </Panel>
            )}
            <Panel title="staff assignments" testId="ov-staff">
              <div className="p-3 space-y-1.5 text-sm">
                <div className="grid grid-cols-[1fr_auto] gap-2"><span className="font-medium">{c.current_judge_name || "Unassigned"}</span><span className="text-xs text-slate-500">Judicial Officer</span></div>
                {(ws.parties || []).filter((p) => p.attorney_name).slice(0, 4).map((p) => (
                  <div key={p.id} className="grid grid-cols-[1fr_auto] gap-2"><span className="font-medium">{p.attorney_name}</span><span className="text-xs text-slate-500">Counsel — {p.display_name}</span></div>
                ))}
              </div>
            </Panel>
            <Panel title="flags & actions due" testId="ov-flags">
              <div className="p-3 space-y-1.5 text-sm">
                {issuedWarrants.map((w) => (
                  <div key={w.id} className="flex items-center gap-1.5 text-red-700 font-semibold text-xs" data-testid={`ov-flag-warrant-${w.id}`}><Flag className="w-3.5 h-3.5 fill-red-600 text-red-700" /> Active {w.warrant_type} Warrant</div>
                ))}
                {custody?.in_custody && <div className="flex items-center gap-1.5 text-orange-700 font-semibold text-xs" data-testid="ov-flag-custody"><Flag className="w-3.5 h-3.5 fill-orange-500 text-orange-600" /> Defendant In Custody</div>}
                {(custody?.active_holds || []).map((h) => (
                  <div key={h.id} className="flex items-center gap-1.5 text-amber-700 font-semibold text-xs"><Flag className="w-3.5 h-3.5 fill-amber-500 text-amber-600" /> Hold: {h.hold_type}</div>
                ))}
                {openTasks.slice(0, 4).map((t) => (
                  <div key={t.id} className="text-xs flex items-center gap-2"><Badge value={t.priority} /><span className="line-clamp-1">{t.title}</span></div>
                ))}
                {!openTasks.length && !issuedWarrants.length && !custody?.in_custody && <p className="text-xs text-slate-400">No flags or actions due.</p>}
              </div>
            </Panel>
            <Panel title="primary parties" testId="ov-parties">
              <div className="p-3 space-y-1.5">
                {ws.parties.slice(0, 6).map((p) => (
                  <div key={p.id} className="flex items-center gap-2 text-sm"><Badge value={p.relationship_type} /><span>{p.display_name}</span></div>
                ))}
              </div>
            </Panel>
            <Panel title="status history" testId="ov-status-history">
              <div className="p-3 space-y-1.5">
                {ws.status_history.slice(0, 5).map((s) => (
                  <div key={s.id} className="text-xs flex items-center gap-2"><Badge value={s.status} /><span className="text-slate-500">{s.reason}</span></div>
                ))}
              </div>
            </Panel>
          </div>
        </div>
      )}

      {tab === "Parties" && (
        <Panel title="Case parties" testId="tab-parties"
          action={can(access, "case.modify") && <button className={btnGhost} onClick={() => setDialog("party")}>Add Party</button>}>
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Relationship</th><th className={thCls}>Party</th><th className={thCls}>DOB</th><th className={thCls}>Counsel</th></tr></thead>
            <tbody>
              {ws.parties.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className={tdCls}><Badge value={p.relationship_type} /></td>
                  <td className={tdCls}>{p.display_name}</td>
                  <td className={`${tdCls} font-mono text-xs`}>{p.person?.dob || "—"}</td>
                  <td className={tdCls}>{p.attorney_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {tab === "Charges" && <ChargesTab caseId={id} access={access} />}

      {tab === "Work Product" && <WorkproductTab caseId={id} />}

      {tab === "Pretrial" && <PretrialTab caseId={id} access={access} />}

      {tab === "Probation" && <ProbationTab caseId={id} access={access} />}

      {tab === "Finance" && <FinanceTab caseId={id} access={access} />}

      {tab === "Evidence" && <EvidenceTab caseId={id} access={access} />}

      {tab === "Discovery" && <DiscoveryTab caseId={id} access={access} />}

      {tab === "Docket" && (
        <Panel title="Official docket" testId="tab-docket"
          action={
            <div className="flex gap-2">
              <button className={btnGhost} onClick={() => setDocketAsc(!docketAsc)} data-testid="docket-order-toggle">{docketAsc ? "Newest first" : "Oldest first"}</button>
              {can(access, "docket.create") && <button className={btnGhost} onClick={() => setDialog("docket")}>Add Entry</button>}
            </div>}>
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Seq</th><th className={thCls}>Date</th><th className={thCls}>Code</th><th className={thCls}>Entry</th><th className={thCls}>Entered By</th><th className={thCls}>Certified</th><th className={thCls}></th></tr></thead>
            <tbody>
              {docket.map((e) => (
                <tr key={e.id} className={cls("hover:bg-slate-50", e.corrected_by_seq && "opacity-60")} data-testid={`docket-entry-${e.sequence_number}`}>
                  <td className={`${tdCls} font-mono text-xs`}>#{e.sequence_number}</td>
                  <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{e.date} {e.time}</td>
                  <td className={`${tdCls} font-mono text-xs`}>{e.event_code}</td>
                  <td className={tdCls}>
                    {e.sealed_masked ? <span className="italic text-slate-400" data-testid={`docket-sealed-${e.sequence_number}`}>{e.description}</span> : e.description}
                    {e.visibility === "SEALED" && !e.sealed_masked && <span className="ml-1.5 text-[10px] font-mono font-bold text-red-700 bg-red-50 border border-red-200 rounded-sm px-1">SEALED</span>}
                    {e.redacted && <span className="ml-1.5 text-[10px] font-mono font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-1">REDACTED</span>}
                    {e.corrected_by_seq && <div className="text-xs text-red-700 font-semibold mt-0.5">Corrected by Docket Entry #{e.corrected_by_seq}</div>}
                    {e.correction_of_seq && <div className="text-xs text-blue-800 font-semibold mt-0.5">Corrective entry for #{e.correction_of_seq}</div>}
                  </td>
                  <td className={`${tdCls} text-xs`}>{e.entered_by}</td>
                  <td className={tdCls}>{e.certified ? <Badge value="ACCEPTED" testId={`docket-certified-${e.sequence_number}`} /> : <span className="text-xs text-slate-400">pending</span>}</td>
                  <td className={`${tdCls} whitespace-nowrap`}>
                    {!e.certified && can(access, "docket.certify") && <button className={btnGhost} onClick={() => certify(e)} data-testid={`docket-certify-btn-${e.sequence_number}`}>Certify</button>}
                    {!e.corrected_by_seq && can(access, "docket.correct") && <button className={`${btnGhost} ml-1`} onClick={() => setCorrecting(e)} data-testid={`docket-correct-btn-${e.sequence_number}`}>Correct</button>}
                    {can(access, "record.seal") && !e.sealed_masked && (
                      <>
                        <button className={`${btnGhost} ml-1`} onClick={() => setSealTarget({ kind: "docket", id: e.id, label: `Docket #${e.sequence_number}`, sealed: e.visibility === "SEALED" })} data-testid={`docket-seal-btn-${e.sequence_number}`}>{e.visibility === "SEALED" ? "Unseal" : "Seal"}</button>
                        <button className={`${btnGhost} ml-1`} onClick={() => setRedacting(e)} data-testid={`docket-redact-btn-${e.sequence_number}`}>Redact</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {tab === "Filings" && (
        <Panel title="Filings" testId="tab-filings"
          action={can(access, "filing.create") && (
            <div className="flex gap-1.5">
              <button className={btnGhost} onClick={() => setDialog("filing")}>Add Filing</button>
            </div>)}>
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Received</th><th className={thCls}>Filing</th><th className={thCls}>Filed By</th><th className={thCls}>Status</th><th className={thCls}>Reason</th><th className={thCls}></th></tr></thead>
            <tbody>
              {ws.filings.map((f) => (
                <tr key={f.id} className="hover:bg-slate-50">
                  <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(f.received_at || f.created_at)}</td>
                  <td className={tdCls}>{f.filing_title || f.title}<div className="text-xs text-slate-400">{f.filing_type}</div></td>
                  <td className={`${tdCls} text-xs`}>{f.filed_by}</td>
                  <td className={tdCls}><Badge value={f.status} /></td>
                  <td className={`${tdCls} text-xs text-red-700`}>{f.rejection_reason || ""}</td>
                  <td className={tdCls}>
                    <button className={btnGhost} onClick={() => ujmsOpenFilingPdf(f.id, f.filing_title || "Filing")}
                      data-testid={`filing-pdf-${f.id}`}><FileText className="w-3.5 h-3.5" /> PDF</button>
                  </td>
                </tr>
              ))}
              {!ws.filings.length && <tr><td colSpan={6} className={tdCls}>No filings.</td></tr>}
            </tbody>
          </table>
          {can(access, "filing.accept") && <p className="px-3 py-2 text-xs text-slate-400">Review pending filings in the <Link to="/ujms/filings" className="text-blue-900 underline">Filing Queue</Link>.</p>}
        </Panel>
      )}

      {tab === "Hearings" && (
        <Panel title="Hearings" testId="tab-hearings"
          action={
            <div className="flex gap-3 items-center">
              <label className="flex items-center gap-1.5 text-[12px] text-white select-none">Sort
                <select className="text-slate-800 text-[12px] h-6 px-1 rounded-[2px] border border-[#74869f] bg-white"
                  value={hearingSort} onChange={(e) => setHearingSort(e.target.value)} data-testid="hearing-sort-order">
                  <option value="DATE">Date Order</option>
                  <option value="TYPE">Type</option>
                </select>
              </label>
              <label className="flex items-center gap-1.5 text-[12px] text-white select-none">Show
                <select className="text-slate-800 text-[12px] h-6 px-1 rounded-[2px] border border-[#74869f] bg-white"
                  value={showFilter} onChange={(e) => setShowFilter(e.target.value)} data-testid="hearing-show-filter">
                  <option value="ALL">All Hearings</option>
                  <option value="FUTURE">Future Only</option>
                </select>
              </label>
              <label className="flex items-center gap-1.5 text-[12px] text-white cursor-pointer select-none">
                <input type="checkbox" checked={showComments} onChange={(e) => setShowComments(e.target.checked)}
                  data-testid="hearing-comments-toggle" /> Hearing Comments
              </label>
              {can(access, "hearing.schedule") && (
                <div className="flex gap-1.5 items-center">
                  <button className={btnGhost} onClick={() => setDialog("findSessions")} data-testid="qa-find-sessions">Find Sessions</button>
                  <button className={btnGhost} onClick={() => setDialog("hearing")}>Schedule Hearing</button>
                  <button className="ody2-btn-blue px-2.5 font-black" title="Add — Find Court Sessions"
                    onClick={() => setDialog("findSessions")} data-testid="hearings-add-plus">+</button>
                </div>
              )}
            </div>
          }>
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>When</th><th className={thCls}>Type</th><th className={thCls}>Judge</th><th className={thCls}>Courtroom</th><th className={thCls}>Status</th><th className={thCls}>Appearance</th><th className={thCls}>Outcome</th><th className={thCls}></th></tr></thead>
            <tbody>
              {visibleHearings.map((h) => (
                <React.Fragment key={h.id}>
                <tr className="hover:bg-slate-50">
                  <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(h.scheduled_start)}</td>
                  <td className={tdCls}>{h.hearing_type_label || h.hearing_type}</td>
                  <td className={`${tdCls} text-xs`}>{h.judicial_officer || "—"}</td>
                  <td className={`${tdCls} text-xs`}>{h.courtroom || "—"}</td>
                  <td className={tdCls}><Badge value={h.status} /></td>
                  <td className={tdCls} data-testid={`hearing-appearance-${h.id}`}>{h.defendant_appearance ? <Badge value={h.defendant_appearance} /> : "—"}</td>
                  <td className={`${tdCls} text-xs`}>{h.outcome || h.result || "—"}</td>
                  <td className={tdCls}>
                    {!["COMPLETED", "CANCELLED", "CONTINUED", "NO_APPEARANCE"].includes(h.status) && can(access, "hearing.manage") && (
                      <button className={btnGhost} onClick={() => setManageH({ ...h, case_id: id, case_number: ws.case?.case_number })}
                        data-testid={`hearing-manage-${h.id}`}>Manage</button>
                    )}
                  </td>
                </tr>
                {showComments && (h.notes || h.continuance_reason) && (
                  <tr className="bg-[#f7fafd]" data-testid={`hearing-comment-${h.id}`}>
                    <td className={cls(tdCls, "text-[12px] italic text-slate-500")} colSpan={8}>
                      <b className="not-italic text-slate-600">Comment:</b> {h.notes || h.continuance_reason}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              {!visibleHearings.length && <tr><td colSpan={8} className={tdCls}>{showFilter === "FUTURE" ? "No future hearings — switch Show to All Hearings to see past settings." : "No hearings."}</td></tr>}
            </tbody>
          </table>
        </Panel>
      )}

      {tab === "Conditions" && <ConditionsTab caseId={id} />}

      {tab === "Custody" && <CustodyBondTab caseId={id} access={access} onChanged={load} />}

      {tab === "Motions" && (
        <Panel testId="tab-motions">
          <EmptyState icon={Gavel} title="Motions & briefing arrive in a later phase" note="Filed motions are tracked as filings for now; the full motion lifecycle (responses, rulings) is scheduled on the roadmap." testId="motions-empty" />
        </Panel>
      )}

      {tab === "Orders" && (
        <Panel title="Orders" testId="tab-orders"
          action={can(access, "order.create") && (
            <button className={btnGhost} onClick={() => setDialog("draftOrder")} data-testid="draft-order-btn">Draft Order</button>)}>
          {ws.orders.length === 0 ? (
            <EmptyState icon={Landmark} title="No orders entered" note="Draft the first order with the button above — cite court rules directly in the order text." testId="orders-empty" />
          ) : (
            <table className="w-full border-collapse">
              <thead><tr><th className={thCls}>Entered</th><th className={thCls}>Order</th><th className={thCls}>Signed By</th><th className={thCls}>Status</th><th className={thCls}></th></tr></thead>
              <tbody>{ws.orders.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50"><td className={`${tdCls} font-mono text-xs`}>{fmtDT(o.created_at)}</td>
                  <td className={tdCls}>{o.title}</td><td className={`${tdCls} text-xs`}>{o.signed_by || "—"}</td><td className={tdCls}><Badge value={o.status} /></td>
                  <td className={tdCls}><button className={btnGhost} onClick={() => ujmsOpenOrderPdf(o.id, o.title)} data-testid={`order-pdf-${o.id}`}><FileText className="w-3.5 h-3.5" /> View PDF</button>
                    {o.status === "ENTERED" && can(access, "docket.certify", "filing.accept") && (
                      <button className={`${btnGhost} ml-1`} onClick={() => ujmsOpenOrderCertified(o.id, o.title)}
                        title="Issue a certified copy with the official certification page"
                        data-testid={`order-certified-${o.id}`}><BadgeCheck className="w-3.5 h-3.5" /> Certified Copy</button>)}
                  </td></tr>
              ))}</tbody>
            </table>
          )}
        </Panel>
      )}

      {tab === "Documents" && (
        <Panel title="Document registry" testId="tab-documents">
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Uploaded</th><th className={thCls}>Title</th><th className={thCls}>Type</th><th className={thCls}>Size</th><th className={thCls}>By</th><th className={thCls}></th></tr></thead>
            <tbody>
              {ws.documents.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50">
                  <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(d.uploaded_at)}</td>
                  <td className={tdCls}>{d.title}
                    {(d.version || 1) > 1 && <span className="ml-1.5 text-[10px] font-mono font-bold text-ujms bg-blue-50 border border-blue-200 rounded-sm px-1" data-testid={`doc-version-badge-${d.id}`}>v{d.version}</span>}
                    {d.public_access && <span className="ml-1.5 text-[10px] font-mono font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1" data-testid={`doc-public-badge-${d.id}`}>PUBLIC</span>}
                    {d.security_level === "SEALED" && <span className="ml-1.5 text-[10px] font-mono font-bold text-red-700 bg-red-50 border border-red-200 rounded-sm px-1" data-testid={`doc-sealed-${d.id}`}>SEALED</span>}
                    <div className="text-xs text-slate-400">{d.filename}</div></td>
                  <td className={`${tdCls} text-xs`}>{d.document_type}</td>
                  <td className={`${tdCls} font-mono text-xs`}>{d.size ? `${Math.round(d.size / 1024)} KB` : "—"}</td>
                  <td className={`${tdCls} text-xs`}>{d.uploaded_by}</td>
                  <td className={tdCls}><button className={btnGhost} onClick={() => ujmsOpenDocument(d.id, d.title || "Court Document")} data-testid={`doc-open-${d.id}`}><FileText className="w-3.5 h-3.5" /> Open PDF</button>
                    {can(access, "filing.create", "case.modify") && d.security_level !== "SEALED" &&
                      <button className={`${btnGhost} ml-1`} onClick={() => setVersionDoc(d)} data-testid={`doc-version-btn-${d.id}`}>New Version</button>}
                    {can(access, "filing.accept", "case.modify") && d.security_level !== "SEALED" &&
                      <button className={cls("ml-1", d.public_access
                        ? "inline-flex items-center gap-1 border border-emerald-400 bg-emerald-50 text-emerald-700 text-sm font-medium px-3 py-1.5 rounded-sm hover:bg-emerald-100"
                        : btnGhost)}
                        onClick={async () => {
                          try {
                            await ujmsSetDocumentPublic(d.id, !d.public_access);
                            toast.success(d.public_access ? "Removed from public portal" : "Released to the public portal");
                            load();
                          } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                        }} data-testid={`doc-public-btn-${d.id}`}>
                        {d.public_access ? "Public ✓" : "Make Public"}
                      </button>}
                    {can(access, "record.seal") && <button className={`${btnGhost} ml-1`} onClick={() => setSealTarget({ kind: "document", id: d.id, label: d.title, sealed: d.security_level === "SEALED" })} data-testid={`doc-seal-btn-${d.id}`}>{d.security_level === "SEALED" ? "Unseal" : "Seal"}</button>}
                  </td>
                </tr>
              ))}
              {!ws.documents.length && <tr><td colSpan={6} className={tdCls}>No documents registered.</td></tr>}
            </tbody>
          </table>
        </Panel>
      )}

      {tab === "Tasks" && (
        <Panel title="Case tasks" testId="tab-tasks">
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Task</th><th className={thCls}>Priority</th><th className={thCls}>Due</th><th className={thCls}>Status</th><th className={thCls}></th></tr></thead>
            <tbody>
              {ws.tasks.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className={tdCls}>{t.title}<div className="text-xs text-slate-400 font-mono">{t.type}</div></td>
                  <td className={tdCls}><Badge value={t.priority} /></td>
                  <td className={`${tdCls} font-mono text-xs`}>{t.due_date}</td>
                  <td className={tdCls}><Badge value={t.status} /></td>
                  <td className={tdCls}>{["OPEN", "IN_PROGRESS"].includes(t.status) && can(access, "task.manage") &&
                    <button className={btnGhost} onClick={() => completeTask(t)} data-testid={`task-complete-${t.id}`}>Complete</button>}</td>
                </tr>
              ))}
              {!ws.tasks.length && <tr><td colSpan={5} className={tdCls}>No tasks for this case.</td></tr>}
            </tbody>
          </table>
        </Panel>
      )}

      {tab === "History" && (
        <Panel title="Case history — operational system events (distinct from the official docket)" testId="tab-history">
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Timestamp</th><th className={thCls}>Event</th><th className={thCls}>Detail</th><th className={thCls}>Actor</th></tr></thead>
            <tbody>
              {ws.events.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50">
                  <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(e.timestamp)}</td>
                  <td className={tdCls}><Badge value={e.event_type} /></td>
                  <td className={`${tdCls} text-xs`}>{Object.entries(e.metadata || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</td>
                  <td className={`${tdCls} text-xs`}>{e.actor_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {tab === "Detail" && <DetailTab c={c} meta={meta} custody={custody} />}
      {tab === "Events" && <EventsTab caseId={id} />}
      {tab === "Service" && <ServiceTab caseId={id} ws={ws} access={access} />}
      {tab === "Causes" && <CausesTab caseId={id} ws={ws} access={access} />}
      {tab === "Disposition" && <DispositionTab caseId={id} />}
      {tab === "Notes" && <NotesTab caseId={id} />}
      </div>

      <OdysseyRail c={c} openTasks={openTasks.length} setTab={setTab} onForms={() => setDialog("selectDoc")} recent={railRecent} />

      {/* Dialogs */}
      {cfg && <>
        <AddPartyDialog open={dialog === "party"} onClose={() => setDialog(null)} caseId={id} cfg={cfg} onDone={load} />
        <ScheduleHearingDialog open={dialog === "hearing"} onClose={() => setDialog(null)} caseId={id} cfg={cfg} c={c} onDone={load} />
        <AddFilingDialog open={dialog === "filing"} onClose={() => setDialog(null)} caseId={id} cfg={cfg} onDone={load} />
        <DocketEntryDialog open={dialog === "docket"} onClose={() => setDialog(null)} caseId={id} onDone={load} presets={isJudicial(access) ? BENCH_DOCKET_PRESETS : undefined} />
        <DraftOrderDialog open={dialog === "draftOrder"} onClose={() => setDialog(null)} caseId={id} onDone={load} />
        <ManageHearingDialog open={!!manageH} onClose={() => setManageH(null)} hearing={manageH} onDone={load} />
        <AssignDialog open={dialog === "assign"} onClose={() => setDialog(null)} caseId={id} cfg={cfg} c={c} onDone={load} />
        <StatusDialog open={dialog === "status"} onClose={() => setDialog(null)} caseId={id} c={c} onDone={load} />
        <DisposeDialog open={dialog === "dispose"} onClose={() => setDialog(null)} caseId={id} caseType={c.case_type} caseNumber={c.case_number} onDone={() => { setDialog(null); load(); }} />
        <SentenceDialog open={dialog === "sentence"} onClose={() => setDialog(null)} caseId={id} caseNumber={c.case_number}
          charges={ws.charges || []} onDone={() => { setDialog(null); load(); }} />
        <MergeCaseDialog open={dialog === "merge"} onClose={() => setDialog(null)} caseId={id} caseNumber={c.case_number}
          judicial={isJudicial(access)} onDone={() => { setDialog(null); load(); }} />
        <CorrectDocketDialog entry={correcting} onClose={() => setCorrecting(null)} caseId={id} onDone={load} />
        <SealDialog target={sealTarget} onClose={() => setSealTarget(null)} onDone={() => { setSealTarget(null); load(); }} />
        <RedactDialog entry={redacting} onClose={() => setRedacting(null)} onDone={() => { setRedacting(null); load(); }} />
      </>}
      <AiSummaryDialog open={dialog === "ai"} onClose={() => setDialog(null)} caseId={id} caseNumber={c.case_number} />
      <AttendanceLetterDialog open={dialog === "attendance"} onClose={() => setDialog(null)} caseId={id} c={c} ws={ws}
        onDone={() => { setDialog(null); load(); }} />
      <UnmergeDialog open={dialog === "unmerge"} onClose={() => setDialog(null)} c={c} onDone={() => { setDialog(null); load(); }} />
      <NewVersionDialog doc={versionDoc} onClose={() => setVersionDoc(null)} onDone={() => { setVersionDoc(null); load(); }} />
      <CustodyActionDialog mode={custodyDialog} onClose={() => setCustodyDialog(null)} custody={custody} caseId={id} onDone={load} />
      <SelectDocumentDialog open={dialog === "selectDoc"} onClose={() => setDialog(null)} caseId={id} caseNumber={c.case_number} />
      {cfg && <FindSessionsDialog open={dialog === "findSessions"} onClose={() => setDialog(null)} caseId={id} c={c} cfg={cfg}
        filings={ws.filings || []} onDone={load} />}
    </div>
  );
}

export function MergeCaseDialog({ open, onClose, caseId, caseNumber, onDone, judicial = false, initialInitials = "" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [source, setSource] = useState(null);
  const [reason, setReason] = useState("");
  const [initials, setInitials] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setQ(""); setResults([]); setSource(null); setReason(""); setInitials(initialInitials || ""); } }, [open, initialInitials]);
  const search = async (v) => {
    setQ(v);
    if (v.length < 2) { setResults([]); return; }
    try {
      const r = await ujmsSearchCases({ q: v, page_size: 6 });
      setResults(r.data.items.filter((x) => x.id !== caseId && x.status !== "MERGED"));
    } catch (e) { /* non-blocking */ }
  };
  const merge = async () => {
    setBusy(true);
    try {
      const r = await ujmsMergeCases(caseId, source.id, reason, judicial ? initials.trim() : "");
      const moved = r.data.moved;
      toast.success(`CASE_MERGED — ${r.data.source_case_number} merged into ${caseNumber}`);
      toast.success(`Transferred: ${Object.entries(moved).map(([k, v]) => `${v} ${k}`).join(", ") || "no records"}`);
      if (r.data.signed) toast.success("CONSOLIDATION_ORDER docketed on both cases");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Merge failed"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="merge-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Merge className="w-5 h-5" /> Merge a Case Into {caseNumber}</DialogTitle></DialogHeader>
        {!source ? (
          <>
            <Field label="Find the case to merge (it will be closed as MERGED)" required>
              <input className={inputCls} value={q} onChange={(e) => search(e.target.value)}
                placeholder="Search by case number or caption…" data-testid="merge-search-input" />
            </Field>
            <div className="space-y-1 max-h-56 overflow-y-auto">
              {results.map((r) => (
                <button key={r.id} onClick={() => setSource(r)} data-testid={`merge-pick-${r.id}`}
                  className="w-full text-left border border-slate-200 rounded-sm px-2.5 py-1.5 hover:border-ujms hover:bg-ujms-soft transition-colors">
                  <span className="font-mono text-xs text-ujms font-semibold">{r.case_number}</span>
                  <span className="text-sm ml-2">{r.caption}</span>
                  <Badge value={r.status} />
                </button>
              ))}
              {q.length >= 2 && !results.length && <div className="text-xs text-slate-400 px-1">No matching open cases.</div>}
            </div>
          </>
        ) : (
          <>
            <div className="border border-violet-300 bg-violet-50 rounded-sm p-2.5 text-sm" data-testid="merge-confirm-summary">
              <b className="font-mono">{source.case_number}</b> — {source.caption}
              <div className="text-xs text-violet-800 mt-1">
                All charges, parties, hearings, filings, documents, orders, motions, tasks, warrants, conditions,
                bonds and custody records will transfer to <b>{caseNumber}</b>. The source case is closed as MERGED
                (its docket is preserved). This action is docketed on both cases.
              </div>
              <button className="text-xs underline text-slate-500 mt-1" onClick={() => setSource(null)} data-testid="merge-change-source">choose a different case</button>
            </div>
            <Field label="Merge reason (docketed)" required>
              <input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Duplicate filing — same defendant and incident" data-testid="merge-reason-input" />
            </Field>
            {judicial && (
              <Field label="Judge initials — signs the consolidation order (docketed on both cases)" required>
                <input className={`${inputCls} uppercase w-32`} value={initials} maxLength={4}
                  onChange={(e) => setInitials(e.target.value.toUpperCase())}
                  placeholder="e.g. ELR" data-testid="merge-initials-input" />
              </Field>
            )}
          </>
        )}
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={!source || !reason.trim() || (judicial && !initials.trim()) || busy} onClick={merge} data-testid="merge-confirm-btn">
            {busy ? "Merging…" : judicial ? "Order Consolidation" : "Merge Cases"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function NewVersionDialog({ doc, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);
  useEffect(() => {
    setFile(null); setNote(""); setHistory(null);
    if (doc) ujmsDocumentVersions(doc.id).then((r) => setHistory(r.data)).catch(() => {});
  }, [doc]);
  if (!doc) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const rd = new FileReader();
        rd.onload = () => res(String(rd.result).split(",")[1]);
        rd.onerror = rej;
        rd.readAsDataURL(file);
      });
      const r = await ujmsAddDocumentVersion(doc.id, {
        content_b64: b64, filename: file.name, mime_type: file.type || "application/pdf", note });
      toast.success(`Version ${r.data.version} stored — previous versions preserved`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="version-dialog">
        <DialogHeader><DialogTitle>New Version — {doc.title}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500">
          Current: v{doc.version || 1} · Superseded versions stay retrievable — nothing is ever deleted.
        </div>
        {history && history.versions.length > 0 && (
          <div className="max-h-28 overflow-y-auto border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="version-history">
            {history.versions.map((v) => (
              <div key={v.id} className="flex items-center gap-2 px-2 py-1 text-xs">
                <span className="font-mono font-bold text-ujms">v{v.version}</span>
                <span className="flex-1 truncate">{v.filename}</span>
                <span className="text-slate-400">{(v.uploaded_at || "").slice(0, 10)}</span>
                <button className="text-ujms underline" data-testid={`version-open-${v.version}`}
                  onClick={() => ujmsOpenDocumentVersion(doc.id, v.version, `${doc.title} (v${v.version})`)}>open</button>
              </div>
            ))}
          </div>
        )}
        <Field label="Replacement file (≤4MB)" required>
          <input type="file" className="text-sm" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="version-file-input" />
        </Field>
        <Field label="Version note">
          <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Corrected exhibit page order" data-testid="version-note-input" />
        </Field>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={!file || busy} onClick={submit} data-testid="version-submit-btn">
            {busy ? "Uploading…" : "Store New Version"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AttendanceLetterDialog({ open, onClose, caseId, c, ws, onDone }) {
  const [hearingId, setHearingId] = useState("");
  const [rtype, setRtype] = useState("DEFENDANT");
  const [partyName, setPartyName] = useState("");
  const [otherName, setOtherName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [issuedTo, setIssuedTo] = useState("");
  const [timeFrom, setTimeFrom] = useState("09:00");
  const [timeTo, setTimeTo] = useState("12:30");
  const [body, setBody] = useState("");
  const [edited, setEdited] = useState(false);
  const [prev, setPrev] = useState([]);
  const [busy, setBusy] = useState(false);

  const hearings = (ws?.hearings || []).slice().sort((a, b) => (b.scheduled_start || "").localeCompare(a.scheduled_start || ""));
  const defendants = (ws?.parties || []).filter((p) =>
    ["DEFENDANT", "RESPONDENT"].includes((p.relationship_type || "").toUpperCase()));
  const hearing = hearings.find((h) => h.id === hearingId);
  const maskedCn = (() => {
    const s = c?.case_number || "";
    return s.length > 7 ? s.slice(0, 5) + "•".repeat(s.length - 7) + s.slice(-2) : s;
  })();
  const recipientName = rtype === "DEFENDANT" ? partyName : otherName;

  const template = useCallback(() => {
    if (!hearing || !recipientName) return "";
    const rel = rtype === "OTHER" && relationship ? `, ${relationship},` : "";
    const capacity = rtype === "DEFENDANT" ? "as a party to the proceedings" : "in support of a party to the proceedings";
    return `TO WHOM IT MAY CONCERN:

This is to certify that ${recipientName}${rel} appeared before the Circuit Court of Cook County, Illinois on ${(hearing.scheduled_start || "").slice(0, 10)} ${capacity} in connection with Case No. ${maskedCn} (${hearing.hearing_type_label || hearing.hearing_type}), before ${c.current_judge_name || "the assigned judicial officer"}${c.current_courtroom_name ? `, ${c.current_courtroom_name}` : ""}. The above-named individual was present at court from ${timeFrom} to ${timeTo}.

Please excuse this absence from work, school, or other obligations on the above date.${issuedTo ? ` This letter is issued for presentation to ${issuedTo}.` : ""} Certified records of this court appearance are maintained by the Office of the Clerk of the Circuit Court and may be verified by citing the letter number shown above.`;
  }, [hearing, recipientName, rtype, relationship, maskedCn, c, timeFrom, timeTo, issuedTo]);

  useEffect(() => {
    if (!open) return;
    setHearingId(hearings[0]?.id || "");
    setRtype("DEFENDANT");
    setPartyName(defendants[0]?.display_name || "");
    setOtherName(""); setRelationship(""); setIssuedTo("");
    setTimeFrom("09:00"); setTimeTo("12:30"); setBody(""); setEdited(false);
    ujmsAttendanceLetters(caseId).then((r) => setPrev(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => { if (open && !edited) setBody(template()); }, [open, edited, template]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await ujmsCreateAttendanceLetter(caseId, {
        hearing_id: hearingId, recipient_type: rtype, recipient_name: recipientName,
        relationship: rtype === "OTHER" ? relationship : "", issued_to: issuedTo,
        time_from: timeFrom, time_to: timeTo, body_text: body });
      toast.success(`Attendance letter ${r.data.letter_number} issued (docketed)`);
      ujmsOpenAttendanceLetterPdf(r.data.id, `${r.data.letter_number} — ${recipientName}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="attendance-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> Attendance Verification Letter — {c.case_number}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500">Form CCG 0651 style · case number prints partially masked ({maskedCn}) · issuance is docketed and audited.</div>
        {prev.length > 0 && (
          <div className="max-h-20 overflow-y-auto border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="attendance-prev">
            {prev.map((L) => (
              <div key={L.id} className="flex items-center gap-2 px-2 py-1 text-xs">
                <span className="font-mono font-bold text-ujms">{L.letter_number}</span>
                <span className="flex-1 truncate">{L.recipient_name} · {L.hearing_date}</span>
                <button className="text-ujms underline" onClick={() => ujmsOpenAttendanceLetterPdf(L.id, L.letter_number)}
                  data-testid={`attendance-reprint-${L.letter_number}`}>reprint</button>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Hearing attended" required>
            <select className={selectCls} value={hearingId} onChange={(e) => { setHearingId(e.target.value); setEdited(false); }} data-testid="attendance-hearing-select">
              {hearings.map((h) => <option key={h.id} value={h.id}>
                {(h.scheduled_start || "").slice(0, 10)} — {h.hearing_type_label || h.hearing_type} ({h.status})</option>)}
            </select>
          </Field>
          <Field label="Letter is for" required>
            <select className={selectCls} value={rtype} onChange={(e) => { setRtype(e.target.value); setEdited(false); }} data-testid="attendance-recipient-type">
              <option value="DEFENDANT">The defendant / party</option>
              <option value="OTHER">Family member / other person</option>
            </select>
          </Field>
        </div>
        {rtype === "DEFENDANT" ? (
          <Field label="Party" required>
            <select className={selectCls} value={partyName} onChange={(e) => { setPartyName(e.target.value); setEdited(false); }} data-testid="attendance-party-select">
              {defendants.map((p) => <option key={p.id} value={p.display_name}>{p.display_name} ({p.relationship_type})</option>)}
              {!defendants.length && <option value="">No defendant on this case</option>}
            </select>
          </Field>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Person's name" required>
              <input className={inputCls} value={otherName} onChange={(e) => { setOtherName(e.target.value); setEdited(false); }}
                placeholder="e.g. Denise Coleman" data-testid="attendance-other-name" />
            </Field>
            <Field label="Relationship to the case">
              <input className={inputCls} value={relationship} onChange={(e) => { setRelationship(e.target.value); setEdited(false); }}
                placeholder="e.g. mother of the defendant" data-testid="attendance-relationship" />
            </Field>
          </div>
        )}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Present from">
            <input type="time" className={inputCls} value={timeFrom} onChange={(e) => { setTimeFrom(e.target.value); setEdited(false); }} data-testid="attendance-time-from" />
          </Field>
          <Field label="Present until">
            <input type="time" className={inputCls} value={timeTo} onChange={(e) => { setTimeTo(e.target.value); setEdited(false); }} data-testid="attendance-time-to" />
          </Field>
          <Field label="Presented to (employer / school)">
            <input className={inputCls} value={issuedTo} onChange={(e) => { setIssuedTo(e.target.value); setEdited(false); }}
              placeholder="optional" data-testid="attendance-issued-to" />
          </Field>
        </div>
        <Field label="Letter body — edit freely before issuing" required>
          <textarea rows={9} className={`${inputCls} h-auto font-serif text-[13px] leading-relaxed`} value={body}
            onChange={(e) => { setBody(e.target.value); setEdited(true); }} data-testid="attendance-body" />
          {edited && <button className="text-xs text-ujms underline mt-1" onClick={() => setEdited(false)} data-testid="attendance-reset-template">Reset to standard template</button>}
        </Field>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={busy || !hearingId || !recipientName.trim() || !body.trim()}
            onClick={submit} data-testid="attendance-submit-btn">
            {busy ? "Issuing…" : "Issue & Print Letter"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UnmergeDialog({ open, onClose, c, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  const run = async () => {
    setBusy(true);
    try {
      const r = await ujmsUnmergeCase(c.id, reason);
      toast.success(`CASE_MERGE_REVERSED — ${r.data.case_number} restored to ${r.data.restored_status}`);
      toast.success(`Restored: ${Object.entries(r.data.moved_back).map(([k, v]) => `${v} ${k}`).join(", ") || "no records"}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Undo failed"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="unmerge-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Merge className="w-5 h-5" /> Undo Merge — {c.case_number}</DialogTitle></DialogHeader>
        <div className="text-sm text-slate-600">
          This restores <b>{c.case_number}</b> to its pre-merge status and moves every transferred record back
          from <b>{c.merged_into_case_number}</b>. Available for 24 hours after a merge; the reversal is docketed
          on both cases and audited.
        </div>
        <Field label="Reversal reason (docketed)" required>
          <input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Wrong case selected during consolidation" data-testid="unmerge-reason-input" />
        </Field>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnDanger} disabled={!reason.trim() || busy} onClick={run} data-testid="unmerge-confirm-btn">
            {busy ? "Reversing…" : "Undo Merge"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CustodyActionDialog({ mode, onClose, custody, caseId, onDone }) {
  const [facility, setFacility] = useState("Cook County Jail — Division 5");
  const [reason, setReason] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const overrideRec = custody?.record || custody?.conditional || custody?.last_release;
  const currentStatus = custody?.in_custody ? "IN_CUSTODY" : custody?.conditional ? "CONDITIONAL_RELEASE" : custody?.last_release ? "RELEASED" : null;
  const statusOptions = ["IN_CUSTODY", "CONDITIONAL_RELEASE", "RELEASED"].filter((s) => s !== currentStatus);
  useEffect(() => { setReason(""); setNewStatus(""); }, [mode]);
  if (!mode) return null;
  const book = async () => {
    setBusy(true);
    try {
      const d = custody?.defendant;
      await ujmsBookPerson({ person_id: d?.person_id || undefined, case_id: caseId, facility,
        notes: reason, ...(d?.person_id ? {} : { first_name: (d?.display_name || "Unknown").split(" ")[0], last_name: (d?.display_name || "Unknown").split(" ").slice(1).join(" ") || "Unknown" }) });
      toast.success("CUSTODY_BOOKED — docketed"); onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Booking failed"); }
    setBusy(false);
  };
  const release = async () => {
    setBusy(true);
    try {
      await ujmsReleasePerson(custody.record.id, { reason: reason || "Released per court order", authority_type: "COURT_ORDER", authority_reference: "Court custody action" });
      toast.success("CUSTODY_RELEASED — docketed"); onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Release failed"); }
    setBusy(false);
  };
  const override = async () => {
    setBusy(true);
    try {
      const r = await ujmsOverrideCustodyStatus(overrideRec.id, { new_status: newStatus, reason });
      toast.success(`CUSTODY_STATUS_OVERRIDDEN — now ${r.data.status.replace(/_/g, " ")} (docketed)`);
      onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Override failed"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="custody-dialog">
        <DialogHeader><DialogTitle>{mode === "book" ? "Book Defendant Into Custody" : mode === "override" ? `Override Custody Status — ${overrideRec?.person_name || "Defendant"}` : `Release ${custody?.record?.person_name || "Defendant"} From Custody`}</DialogTitle></DialogHeader>
        {mode === "book" ? (
          <>
            <div className="text-sm text-slate-600">Defendant: <b>{custody?.defendant?.display_name || "—"}</b></div>
            <Field label="Facility" required><input className={inputCls} value={facility} onChange={(e) => setFacility(e.target.value)} data-testid="custody-facility" /></Field>
            <Field label="Notes"><input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Remanded at hearing" data-testid="custody-notes" /></Field>
          </>
        ) : mode === "override" ? (
          <>
            <div className="text-sm text-slate-600">
              Booking <span className="font-mono">{overrideRec?.booking_number}</span> · {overrideRec?.facility}
              <span className="ml-2 text-xs font-bold uppercase text-slate-500">Current: {(currentStatus || "").replace(/_/g, " ")}</span>
            </div>
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-2.5 py-2">
              Judicial override — changes custody status directly without a book/release round-trip.
              The change is docketed, audited and logged on the custody record. Active holds still block release-type overrides.
            </div>
            <Field label="New status" required>
              <select className={selectCls} value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="custody-override-status">
                <option value="">Select new status…</option>
                {statusOptions.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            </Field>
            {custody?.in_custody && (custody?.active_holds || []).length > 0 && newStatus && newStatus !== "IN_CUSTODY" && (
              <div className="text-xs bg-red-50 border border-red-200 text-red-800 rounded-sm px-2.5 py-2">
                {custody.active_holds.length} active hold(s) will block this override — lift them first.
              </div>
            )}
            <Field label="Override reason (docketed)" required>
              <input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Conditional release ordered from the bench" data-testid="custody-override-reason" />
            </Field>
          </>
        ) : (
          <>
            <div className="text-sm text-slate-600">Booking <span className="font-mono">{custody?.record?.booking_number}</span> · {custody?.record?.facility}</div>
            {(custody?.active_holds || []).length > 0 && (
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-2.5 py-2">
                {custody.active_holds.length} active hold(s) will block this release — lift them from Sheriff / Custody first.
              </div>
            )}
            <Field label="Release reason" required><input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Bond posted; Order of release entered" data-testid="custody-release-reason" /></Field>
          </>
        )}
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          {mode === "book"
            ? <button className={btnPrimary} disabled={busy || !facility.trim()} onClick={book} data-testid="custody-book-confirm">{busy ? "Booking…" : "Book Into Custody"}</button>
            : mode === "override"
              ? <button className={btnPrimary} disabled={busy || !newStatus || !reason.trim()} onClick={override} data-testid="custody-override-confirm">{busy ? "Applying…" : "Apply Override"}</button>
              : <button className={btnDanger} disabled={busy || !reason.trim()} onClick={release} data-testid="custody-release-confirm">{busy ? "Releasing…" : "Confirm Release"}</button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ChargesTab({ caseId, access }) {
  const [charges, setCharges] = useState(null);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState(null);
  const [correcting, setCorrecting] = useState(null);
  const load = useCallback(() => {
    ujmsCaseCharges(caseId).then((r) => setCharges(r.data)).catch(() => setCharges([]));
  }, [caseId]);
  useEffect(() => { load(); }, [load]);
  const canCorrect = can(access, "charge.correct");
  const canManage = can(access, "charge.manage") || canCorrect;
  const cols = canManage ? 9 : 8;
  if (!charges) return <Panel testId="tab-charges"><div className="text-sm text-slate-400 p-4">Loading charges…</div></Panel>;
  return (
    <Panel title="Charges" testId="tab-charges"
      action={canManage && (
        <button className={btnGhost} onClick={() => setForm(newChargeForm())} data-testid="charge-add-count-btn">+ Add Count</button>
      )}>
      {charges.length === 0 ? (
        <EmptyState icon={Scale} title="No charges on record" note="Charges are authorized through the Criminal Pipeline (referral review) or filed directly on the case." testId="charges-empty" />
      ) : (
        <table className="w-full border-collapse">
          <thead><tr>
            <th className={thCls}>Count</th><th className={thCls}>Statute</th><th className={thCls}>Description</th>
            <th className={thCls}>Class</th><th className={thCls}>Offense Date</th><th className={thCls}>Status</th>
            <th className={thCls}>Disposition</th><th className={thCls}>History</th>{canManage && <th className={thCls}></th>}
          </tr></thead>
          <tbody>
            {charges.map((ch) => (
              <React.Fragment key={ch.id}>
                {correcting === ch.id ? <ChargeCorrectionRow caseId={caseId} ch={ch} colSpan={cols} onDone={() => { setCorrecting(null); load(); }} onCancel={() => setCorrecting(null)} /> : (
                <tr className="hover:bg-slate-50" data-testid={`charge-row-${ch.count_number}`}>
                  <td className={`${tdCls} font-mono text-xs`}>Count {ch.count_number}</td>
                  <td className={`${tdCls} font-mono text-xs`}>{ch.statute_code}</td>
                  <td className={tdCls}>{ch.description}</td>
                  <td className={tdCls}>{ch.classification ? <Badge value={ch.classification} /> : "—"}</td>
                  <td className={`${tdCls} font-mono text-xs`}>{ch.offense_date || "—"}</td>
                  <td className={tdCls}><Badge value={ch.status} testId={`charge-status-${ch.count_number}`} /></td>
                  <td className={tdCls}>{ch.disposition ? `${ch.disposition}${ch.disposition_date ? ` (${ch.disposition_date})` : ""}` : "—"}</td>
                  <td className={tdCls}>
                    <button className="text-xs text-blue-900 underline" data-testid={`charge-history-toggle-${ch.count_number}`}
                      onClick={() => setOpen(open === ch.id ? null : ch.id)}>
                      {(ch.history || []).length} event{(ch.history || []).length === 1 ? "" : "s"}
                    </button>
                  </td>
                  {canManage && (
                    <td className={`${tdCls} whitespace-nowrap`}>
                      {can(access, "charge.manage") && AMENDABLE.includes(ch.status) && (
                        <button className={btnGhost} data-testid={`charge-amend-${ch.count_number}`}
                          aria-label={`Amend count ${ch.count_number}`}
                          onClick={() => setForm(amendChargeForm(ch))}>
                          Amend
                        </button>
                      )}
                      {canCorrect && <button className={`${btnGhost} ml-1`} data-testid={`charge-correct-${ch.count_number}`} aria-label={`Correct count ${ch.count_number}`} onClick={() => setCorrecting(ch.id)}>Correct</button>}
                    </td>
                  )}
                </tr>)}
                {open === ch.id && (
                  <tr data-testid={`charge-history-${ch.count_number}`}>
                    <td colSpan={cols} className="bg-slate-50 border-b border-slate-200 px-4 py-3">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-2">Amendment / disposition history (append-only)</div>
                      <div className="space-y-1.5">
                        {(ch.history || []).map((h) => (
                          <div key={h.id} className="text-xs text-slate-600 flex flex-wrap gap-2 items-baseline">
                            <span className="font-mono text-slate-400">{fmtDT(h.created_at)}</span>
                            <Badge value={h.action} />
                            <span>{h.actor}</span>
                            {h.before && Object.keys(h.before).length > 0 && <span className="text-slate-400">from <span className="font-mono">{JSON.stringify(h.before)}</span></span>}
                            {h.after && Object.keys(h.after).length > 0 && <span className="text-slate-400">to <span className="font-mono">{JSON.stringify(h.after)}</span></span>}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
      {form && <ChargeFormDialog form={form} onClose={() => setForm(null)} caseId={caseId} onDone={load} />}
    </Panel>
  );
}

export function WorkproductTab({ caseId }) {
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [text, setText] = useState("");
  const [category, setCategory] = useState("GENERAL");
  const load = useCallback(() => {
    ujmsWorkproduct(caseId).then((r) => setData(r.data)).catch(() => setDenied(true));
  }, [caseId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try {
      await ujmsAddWorkproduct(caseId, { text, category });
      toast.success("Work product note recorded (privileged)");
      setText(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (denied) return (
    <Panel testId="tab-workproduct">
      <EmptyState icon={ShieldAlert} title="Access restricted" note="Work product is visible only to the owning agency's personnel — not to administrators, the court or opposing counsel." testId="workproduct-denied" />
    </Panel>
  );
  if (!data) return <Panel testId="tab-workproduct"><div className="text-sm text-slate-400 p-4">Loading…</div></Panel>;
  return (
    <div className="space-y-3" data-testid="tab-workproduct">
      <div className="bg-red-50 border border-red-200 text-red-800 text-xs rounded-sm px-3 py-2 flex items-center gap-2" data-testid="workproduct-banner">
        <ShieldAlert className="w-4 h-4 shrink-0" />
        PRIVILEGED WORK PRODUCT — {data.scope} personnel only. These notes are never part of the court record and are invisible to every other agency.
      </div>
      <Panel title={`${data.scope === "PROSECUTION" ? "Prosecution" : "Defense"} internal notes`} testId="workproduct-panel">
        <div className="p-3 border-b border-slate-200 space-y-2">
          <div className="flex gap-2">
            <select className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm" value={category}
              onChange={(e) => setCategory(e.target.value)} data-testid="workproduct-category">
              {["GENERAL", "STRATEGY", "WITNESS", "EVIDENCE", "PLEA"].map((c) => <option key={c}>{c}</option>)}
            </select>
            <input className="flex-1 border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm" value={text}
              onChange={(e) => setText(e.target.value)} placeholder="Add an internal note (append-only)…"
              data-testid="workproduct-input" onKeyDown={(e) => e.key === "Enter" && text.trim() && add()} />
            <button className={btnPrimary} onClick={add} disabled={!text.trim()} data-testid="workproduct-add-btn">Add Note</button>
          </div>
        </div>
        {data.notes.length === 0 ? (
          <div className="text-sm text-slate-400 p-4">No internal notes on this case yet.</div>
        ) : (
          <div className="divide-y divide-slate-200">
            {data.notes.map((n) => (
              <div key={n.id} className="px-4 py-2.5 text-sm" data-testid={`workproduct-note-${n.id}`}>
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-0.5">
                  <Badge value={n.category} />
                  <span>{n.author_name}</span>
                  <span className="font-mono">{fmtDT(n.created_at)}</span>
                </div>
                <div className="text-slate-800">{n.text}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

export function AiSummaryDialog({ open, onClose, caseId, caseNumber }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) ujmsAiSummary(caseId).then((r) => setData(r.data)).catch(() => setData({ summary: null }));
  }, [open, caseId]);
  const generate = async () => {
    setBusy(true);
    try {
      const r = await ujmsGenerateAiSummary(caseId);
      setData(r.data);
      toast.success("AI summary generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ai-summary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> AI Case Summary — {caseNumber}</DialogTitle>
        </DialogHeader>
        {busy ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center" data-testid="ai-summary-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Reading the docket and drafting the summary…
          </div>
        ) : data?.summary ? (
          <div className="space-y-2">
            <div className="text-[11px] text-slate-400 font-mono">
              Generated {fmtDT(data.created_at)} by {data.generated_by} · {data.model} · advisory only — not part of the record
            </div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap max-h-[50vh] overflow-y-auto border border-slate-200 rounded-sm bg-slate-50 px-4 py-3" data-testid="ai-summary-text">
              {data.summary}
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-500 py-6 text-center" data-testid="ai-summary-empty">
            No summary yet. Generate one from the current docket, charges, hearings and orders.
          </div>
        )}
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Close</button>
          <button className={btnPrimary} onClick={generate} disabled={busy} data-testid="ai-summary-generate-btn">
            <Sparkles className="w-3.5 h-3.5" /> {data?.summary ? "Regenerate" : "Generate Summary"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const REC_STYLE = {
  RELEASE_ROR: "bg-emerald-50 border-emerald-200 text-emerald-800",
  RELEASE_WITH_CONDITIONS: "bg-sky-50 border-sky-200 text-sky-800",
  SUPERVISION: "bg-amber-50 border-amber-200 text-amber-800",
  DETENTION_HEARING: "bg-red-50 border-red-200 text-red-800",
};

export function PretrialTab({ caseId, access }) {
  const [data, setData] = useState(null);
  const [assessing, setAssessing] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [logging, setLogging] = useState(null);
  const load = useCallback(() => {
    ujmsCasePretrial(caseId).then((r) => setData(r.data)).catch(() => setData({ assessments: [], conditions: [], orders: [] }));
  }, [caseId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Panel testId="tab-pretrial"><div className="text-sm text-slate-400 p-4">Loading…</div></Panel>;

  const terminate = async (cond) => {
    const reason = window.prompt(`Terminate "${cond.condition_label}" — reason (docketed):`);
    if (!reason) return;
    try { await ujmsTerminateCondition(cond.id, reason); toast.success("CONDITION_TERMINATED — docketed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const logCompliance = async () => {
    try {
      await ujmsLogCompliance(logging.cond.id, { status: logging.status, note: logging.note || "" });
      toast.success(logging.status === "VIOLATION" ? "VIOLATION_REPORTED — docketed" : "Compliance logged");
      setLogging(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="tab-pretrial">
      <Panel title="Pretrial risk assessments" testId="pretrial-assessments"
        action={can(access, "pretrial.assess") && (
          <button className={btnPrimary} onClick={() => setAssessing(true)} data-testid="pretrial-new-assessment-btn">New Assessment</button>)}>
        {data.assessments.length === 0 ? (
          <div className="text-sm text-slate-400 p-4">No assessments on file for this case.</div>
        ) : (
          <div className="divide-y divide-slate-200">
            {data.assessments.map((a) => (
              <div key={a.id} className={cls("px-4 py-3", a.superseded_by && "opacity-50")} data-testid={`assessment-${a.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className={cls("text-xs font-bold uppercase tracking-wide border rounded-sm px-2 py-1", REC_STYLE[a.recommendation] || "bg-slate-50 border-slate-200 text-slate-700")}
                    data-testid={`assessment-rec-${a.id}`}>
                    {a.recommendation.replace(/_/g, " ")}
                  </span>
                  <span className="text-xs font-mono text-slate-600">FTA {a.fta_score}/6 · NCA {a.nca_score}/6{a.violence_flag ? " · VIOLENCE FLAG" : ""}</span>
                  {a.superseded_by && <Badge value="SUPERSEDED" />}
                  <span className="text-[11px] text-slate-400 font-mono ml-auto">{fmtDT(a.created_at)} · {a.assessed_by}</span>
                </div>
                {a.narrative && <div className="text-sm text-slate-600 mt-1.5">{a.narrative}</div>}
                <div className="text-[11px] text-slate-400 mt-1">{a.tool}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Structured release conditions" testId="pretrial-conditions"
        action={can(access, "order.sign", "order.create") && (
          <button className={btnGhost} onClick={() => setAttaching(true)} data-testid="pretrial-attach-condition-btn">Attach Condition</button>)}>
        {data.conditions.length === 0 ? (
          <div className="text-sm text-slate-400 p-4">No release conditions attached to orders in this case.</div>
        ) : (
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Condition</th><th className={thCls}>Order</th><th className={thCls}>Status</th><th className={thCls}>Compliance Log</th><th className={thCls}></th></tr></thead>
            <tbody>
              {data.conditions.map((x) => (
                <tr key={x.id} className="hover:bg-slate-50 align-top" data-testid={`condition-${x.id}`}>
                  <td className={tdCls}>{x.condition_label}{x.details && <div className="text-xs text-slate-400">{x.details}</div>}
                    <div className="text-[11px] text-slate-400">Imposed by {x.imposed_by}</div></td>
                  <td className={tdCls}><div className="text-xs">{x.order_title}</div><Badge value={x.order_status} /></td>
                  <td className={tdCls}><Badge value={x.status} testId={`condition-status-${x.id}`} /></td>
                  <td className={tdCls}>
                    {(x.compliance || []).length === 0 ? <span className="text-xs text-slate-400">No entries</span> : (
                      <div className="space-y-1">
                        {(x.compliance || []).slice(0, 4).map((e) => (
                          <div key={e.id} className="text-xs flex items-center gap-1.5">
                            <Badge value={e.status} /><span className="font-mono text-slate-400">{fmtDT(e.created_at)}</span>
                            {e.note && <span className="text-slate-500 truncate max-w-[160px]">{e.note}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className={tdCls}>
                    <div className="flex flex-col gap-1.5">
                      {can(access, "pretrial.assess") && x.status !== "TERMINATED" && <>
                        <button className={btnGhost} onClick={() => setLogging({ cond: x, status: "COMPLIANT", note: "" })} data-testid={`condition-compliant-${x.id}`}>Compliant</button>
                        <button className={btnDanger} onClick={() => setLogging({ cond: x, status: "VIOLATION", note: "" })} data-testid={`condition-violation-${x.id}`}>Violation</button>
                      </>}
                      {can(access, "order.sign") && x.status !== "TERMINATED" &&
                        <button className={btnGhost} onClick={() => terminate(x)} data-testid={`condition-terminate-${x.id}`}>Terminate</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <AssessmentDialog open={assessing} onClose={() => setAssessing(false)} caseId={caseId} onDone={load} />
      <AttachConditionDialog open={attaching} onClose={() => setAttaching(false)} orders={data.orders} onDone={load} />
      <Dialog open={!!logging} onOpenChange={(v) => !v && setLogging(null)}>
        <DialogContent data-testid="condition-compliance-dialog">
          <DialogHeader><DialogTitle>{logging?.status === "VIOLATION" ? "Report violation" : "Log compliance"} — {logging?.cond?.condition_label}</DialogTitle></DialogHeader>
          <Field label="Note">
            <input className={inputCls} value={logging?.note || ""} onChange={(e) => setLogging({ ...logging, note: e.target.value })} data-testid="condition-compliance-note" />
          </Field>
          <DialogFooter>
            <button className={btnGhost} onClick={() => setLogging(null)}>Cancel</button>
            <button className={logging?.status === "VIOLATION" ? btnDanger : btnPrimary} onClick={logCompliance} data-testid="condition-compliance-submit">
              {logging?.status === "VIOLATION" ? "Report Violation" : "Log Compliant"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssessmentDialog({ open, onClose, caseId, onDone }) {
  const blank = { age: 30, prior_ftas: 0, prior_convictions: 0, pending_charges_count: 0,
    employed: false, stable_housing: false, substance_concern: false, violent_offense: false, narrative: "" };
  const [f, setF] = useState(blank);
  const num = (k) => (e) => setF({ ...f, [k]: parseInt(e.target.value || "0", 10) });
  const chk = (k) => (e) => setF({ ...f, [k]: e.target.checked });
  const submit = async () => {
    try {
      const r = await ujmsCreateAssessment(caseId, f);
      toast.success(`Assessment submitted — recommendation: ${r.data.recommendation.replace(/_/g, " ")}`);
      onClose(); onDone(); setF(blank);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="assessment-dialog">
        <DialogHeader><DialogTitle>New pretrial risk assessment</DialogTitle></DialogHeader>
        <div className="text-[11px] text-slate-400">CJMS Pretrial Risk Screen — fictional demonstration instrument. Scores are computed server-side; a submitted assessment is immutable.</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Age"><input type="number" className={inputCls} value={f.age} onChange={num("age")} data-testid="assess-age" /></Field>
          <Field label="Prior failures to appear"><input type="number" className={inputCls} value={f.prior_ftas} onChange={num("prior_ftas")} data-testid="assess-ftas" /></Field>
          <Field label="Prior convictions"><input type="number" className={inputCls} value={f.prior_convictions} onChange={num("prior_convictions")} data-testid="assess-convictions" /></Field>
          <Field label="Other pending charges"><input type="number" className={inputCls} value={f.pending_charges_count} onChange={num("pending_charges_count")} data-testid="assess-pending" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          {[["employed", "Employed / in school"], ["stable_housing", "Stable housing"],
            ["substance_concern", "Substance-use concern"], ["violent_offense", "Current offense is violent"]].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 border border-slate-200 rounded-sm px-2.5 py-1.5 cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={f[k]} onChange={chk(k)} data-testid={`assess-${k}`} /> {label}
            </label>
          ))}
        </div>
        <Field label="Narrative">
          <textarea className={`${inputCls} h-16`} value={f.narrative} onChange={(e) => setF({ ...f, narrative: e.target.value })}
            placeholder="Officer observations…" data-testid="assess-narrative" />
        </Field>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} data-testid="assess-submit">Submit Assessment</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttachConditionDialog({ open, onClose, orders, onDone }) {
  const [types, setTypes] = useState([]);
  const [f, setF] = useState({ order_id: "", condition_type: "", details: "" });
  useEffect(() => { ujmsConditionTypes().then((r) => setTypes(r.data)).catch(() => {}); }, []);
  const submit = async () => {
    try {
      await ujmsAttachCondition(f.order_id, { condition_type: f.condition_type, details: f.details });
      toast.success("CONDITION_IMPOSED — attached to order");
      onClose(); onDone(); setF({ order_id: "", condition_type: "", details: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="attach-condition-dialog">
        <DialogHeader><DialogTitle>Attach release condition to an order</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Order" required>
            <select className={selectCls} value={f.order_id} onChange={(e) => setF({ ...f, order_id: e.target.value })} data-testid="attach-condition-order">
              <option value="">Select order…</option>
              {orders.map((o) => <option key={o.id} value={o.id}>{o.title} [{o.status}]</option>)}
            </select>
          </Field>
          <Field label="Condition" required>
            <select className={selectCls} value={f.condition_type} onChange={(e) => setF({ ...f, condition_type: e.target.value })} data-testid="attach-condition-type">
              <option value="">Select condition…</option>
              {types.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Details">
            <input className={inputCls} value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })}
              placeholder="Report weekly; no contact with…" data-testid="attach-condition-details" />
          </Field>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={!f.order_id || !f.condition_type} data-testid="attach-condition-submit">Attach</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
